import styles from "./ReasonToBuy.module.scss";
import { useRef, useState, useEffect, KeyboardEvent } from "react";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { ChevronLeftOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronLeftOutlined";
import { ChevronRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import classNames from "classnames/bind";
import { Card } from "./ReasonToBuy";
import {
  getCardsPerPage,
  getClosestCardIndex,
  MatchMediaState,
  shouldShowChevronNavigation,
} from "./utils/utils";
import { ArrowRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ArrowRightOutlined";

const cx = classNames.bind(styles);

// 0-599px: single card
const SMALL_MOBILE_SCROLL_MEDIA_QUERY = "(max-width: 599px)";
// 600-767px: large mobile, 1 card + peek
const LARGE_MOBILE_SCROLL_MEDIA_QUERY =
  "(min-width: 600px) and (max-width: 767px)";
// 768-1023px: small tablet, 2 cards + peek
const VERY_SMALL_DT_SCROLL_MEDIA_QUERY =
  "(min-width: 768px) and (max-width: 1023px)";
// 1024-1279px: small DT, 2 cards per chevron page
const SMALL_DESKTOP_SCROLL_MEDIA_QUERY =
  "(min-width: 1024px) and (max-width: 1279px)";
// 1280-1439px: desktop, 2 cards per chevron page
const LARGE_DESKTOP_SCROLL_MEDIA_QUERY =
  "(min-width: 1280px) and (max-width: 1439px)";
// 1440px+: large desktop, full-width no carousel
const LARGE_DESKTOP_ABOVE_SCROLL_MEDIA_QUERY = "(min-width: 1440px)";

type Props = {
  cards: Card[];
};

export const ReasonToBuyUI = ({ cards }: Props) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activePage, setActivePage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(1);
  const [showChevronNavigation, setShowChevronNavigation] = useState(false);
  const isChevronBtnScrollRef = useRef(false);

  useEffect(
    function listenToMediaQueryChanges() {
      const mqSmallMobile = window.matchMedia(SMALL_MOBILE_SCROLL_MEDIA_QUERY);
      const mqLargeMobile = window.matchMedia(LARGE_MOBILE_SCROLL_MEDIA_QUERY);
      const mqVerySmallDt = window.matchMedia(VERY_SMALL_DT_SCROLL_MEDIA_QUERY);
      const mqSmallDesktop = window.matchMedia(
        SMALL_DESKTOP_SCROLL_MEDIA_QUERY,
      );
      const mqLargeDesktop = window.matchMedia(
        LARGE_DESKTOP_SCROLL_MEDIA_QUERY,
      );
      const mqLargeDesktopAbove = window.matchMedia(
        LARGE_DESKTOP_ABOVE_SCROLL_MEDIA_QUERY,
      );

      const update = () => {
        const matchMediaState: MatchMediaState = {
          smallMobile: mqSmallMobile.matches,
          largeMobile: mqLargeMobile.matches,
          verySmallDt: mqVerySmallDt.matches,
          smallDesktop: mqSmallDesktop.matches,
          largeDesktop: mqLargeDesktop.matches,
          largeDesktopAbove: mqLargeDesktopAbove.matches,
        };

        setShowChevronNavigation(shouldShowChevronNavigation(matchMediaState));
        setCardsPerPage(getCardsPerPage(cards.length, matchMediaState));
      };

      const mediaQueries = [
        mqSmallMobile,
        mqLargeMobile,
        mqVerySmallDt,
        mqSmallDesktop,
        mqLargeDesktop,
        mqLargeDesktopAbove,
      ];

      update();

      mediaQueries.forEach((mq) => {
        if (typeof mq.addEventListener === "function") {
          mq.addEventListener("change", update);
        } else if (typeof mq.addListener === "function") {
          mq.addListener(update);
        }
      });
      return () => {
        mediaQueries.forEach((mq) => {
          if (typeof mq.removeEventListener === "function") {
            mq.removeEventListener("change", update);
          } else if (typeof mq.removeListener === "function") {
            mq.removeListener(update);
          }
        });
      };
    },
    [cards.length],
  );

  useEffect(
    function syncActivePageWithScroll() {
      const track = trackRef.current;
      if (!track) return;

      const handleScroll = () => {
        // Skip recalculation if this is a programmatic scroll triggered by chevron/pagination
        if (isChevronBtnScrollRef.current) {
          isChevronBtnScrollRef.current = false;
          return;
        }

        const items = Array.from(
          track.querySelectorAll<HTMLElement>("[data-card-index]"),
        );
        if (!items.length) return;

        const closestIndex = getClosestCardIndex(items, track.scrollLeft);
        const clampedIndex = Math.max(
          0,
          Math.min(closestIndex, items.length - 1),
        );
        const nextPage = Math.min(
          Math.max(Math.floor(clampedIndex / cardsPerPage), 0),
          totalPages - 1,
        );

        setActivePage(nextPage);
      };

      // Sync on mount and whenever cardsPerPage changes
      handleScroll();

      track.addEventListener("scroll", handleScroll, { passive: true });
      return () => track.removeEventListener("scroll", handleScroll);
    },
    [cards.length, cardsPerPage],
  );

  if (!cards.length) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(cards.length / cardsPerPage));

  const scrollToPage = (pageIndex: number) => {
    const track = trackRef.current;
    if (!track) return;

    const items = Array.from(
      track.querySelectorAll<HTMLElement>("[data-card-index]"),
    );
    if (!items.length) return;

    // Calculate the index of the last card on this page
    const lastCardIndexOnPage = Math.min(
      (pageIndex + 1) * cardsPerPage - 1,
      items.length - 1,
    );
    const lastCardOnPage = items[lastCardIndexOnPage];
    if (!lastCardOnPage) return;

    // Scroll to position where the last card of the page is just at the right edge of viewport
    // This ensures all cards on the page are visible
    const containerWidth = track.clientWidth;
    const lastCardRight =
      lastCardOnPage.offsetLeft + lastCardOnPage.offsetWidth;
    const left = Math.max(0, lastCardRight - containerWidth);

    // Mark this as a programmatic scroll so the scroll event handler doesn't recalculate activePage
    isChevronBtnScrollRef.current = true;

    if (typeof track.scrollTo === "function") {
      track.scrollTo({
        left,
        behavior: "smooth",
      });
      return;
    }

    track.scrollLeft = left;
  };

  const goToPage = (pageIndex: number) => {
    const safePageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));
    setActivePage(safePageIndex);
    scrollToPage(safePageIndex);
  };

  const scrollTrackBy = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;

    const items = Array.from(
      track.querySelectorAll<HTMLElement>("[data-card-index]"),
    );
    if (!items.length) return;

    const closestIndex = getClosestCardIndex(items, track.scrollLeft);

    const nextCardIndex = Math.max(
      0,
      Math.min(closestIndex + direction, items.length - 1),
    );

    if (nextCardIndex === closestIndex) {
      return;
    }

    const nextCard = items[nextCardIndex];
    if (!nextCard) return;
    const scrollLeft = Math.max(0, nextCard.offsetLeft);

    const newPage = Math.min(
      Math.max(Math.floor(nextCardIndex / cardsPerPage), 0),
      totalPages - 1,
    );

    setActivePage(newPage);

    if (typeof track.scrollTo === "function") {
      track.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    } else {
      track.scrollLeft = scrollLeft;
    }
  };

  const handleTrackKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      // When chevron navigation is shown, use per-page scrolling like the chevron buttons
      if (showChevronNavigation) {
        goToPage(activePage + 1);
      } else {
        scrollTrackBy(1);
      }
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      // When chevron navigation is shown, use per-page scrolling like the chevron buttons
      if (showChevronNavigation) {
        goToPage(activePage - 1);
      } else {
        scrollTrackBy(-1);
      }
    }
  };

  return (
    <div data-component="ReasonToBuy" className={styles.container}>
      <ul
        className={styles.cards}
        data-testid="reason-to-buy-cards-track"
        ref={trackRef}
        aria-label="Reason to buy cards"
        tabIndex={0}
        onKeyDown={handleTrackKeyDown}
      >
        {cards.map((card, index) => {
          const isCardButton = !!card.onCardClick;

          return (
            <li
              key={card.id}
              data-card-index={index}
              className={cx(styles.card, {
                cardButton: isCardButton,
              })}
              data-testid={`reason-to-buy-card-${card.id}`}
              onClick={card.onCardClick}
              onKeyDown={(event) => {
                if (!isCardButton) return;

                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  card.onCardClick?.();
                }
              }}
              tabIndex={isCardButton ? 0 : undefined}
              aria-label={isCardButton ? card.title : undefined}
              role={isCardButton ? "button" : undefined}
            >
              {isCardButton && (
                <ArrowRightOutlined
                  size="medium"
                  className={styles.cardArrow}
                />
              )}

              <div className={styles.cardHeader}>
                {card.icon && (
                  <span className={styles.cardIcon}>{card.icon}</span>
                )}
              </div>
              <h3 className={styles.cardTitle}>{card.title}</h3>
              {card.onLinkClick ? (
                <UnderlinedButton
                  onClick={card.onLinkClick}
                  className={styles.cardDescriptionButton}
                  data-testid={`reason-to-buy-card-${card.id}-button`}
                  labelText={card.linkText || card.description || ""}
                />
              ) : card.linkUrl && card.linkText ? (
                <UnderlinedButton
                  tag={"a"}
                  className={styles.cardLinkButton}
                  href={card.linkUrl}
                  data-testid={`reason-to-buy-card-${card.id}-link`}
                  labelText={card.linkText || card.description || ""}
                />
              ) : card.description ? (
                <p className={styles.cardDescription}>{card.description}</p>
              ) : null}
            </li>
          );
        })}
      </ul>
      {totalPages > 1 && (
        <div
          className={styles.pagination}
          data-testid="reason-to-buy-pagination"
        >
          {showChevronNavigation ? (
            <>
              <button
                type="button"
                className={styles.paginationButton}
                onClick={() => goToPage(activePage - 1)}
                aria-label="Previous cards"
                disabled={activePage === 0}
              >
                <ChevronLeftOutlined size="small" />
              </button>
              <button
                type="button"
                className={styles.paginationButton}
                onClick={() => goToPage(activePage + 1)}
                aria-label="Next cards"
                disabled={activePage >= totalPages - 1}
              >
                <ChevronRightOutlined size="small" />
              </button>
            </>
          ) : (
            Array.from({ length: totalPages }).map((_, pageIndex) => (
              <span
                key={`pagination-dot-${pageIndex}`}
                className={styles.paginationDot}
                data-active={pageIndex === activePage}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
